import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, CalendarDays, Users, GraduationCap, ExternalLink, LogOut, Building2, FileDown, Upload, ArrowLeft } from 'lucide-react';

const NAV = [
  { to: '/secretariat', label: 'Tableau de bord', icon: LayoutDashboard, exact: true },
  { to: '/secretariat/planning', label: 'Planning', icon: CalendarDays },
  { to: '/secretariat/eleves', label: 'Élèves', icon: GraduationCap },
  { to: '/secretariat/enseignants', label: 'Enseignants', icon: Users },
  { to: '/secretariat/import', label: 'Import planning', icon: Upload },
  { to: '/secretariat/dsr', label: 'Export DSR', icon: FileDown },
];

export default function SecretariatShell({ children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const onLogout = () => { logout(); navigate('/'); };

  return (
    <div className="App min-h-screen bg-[#0a0a0b] text-white flex flex-col md:flex-row" data-testid="secretariat-shell">
      <aside className="hidden md:flex md:w-60 md:min-h-screen shrink-0 border-r border-white/10 bg-[#0d0d0e] flex-col">
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-white/10">
          <div className="w-9 h-9 rounded-xl bg-amber-500 text-black flex items-center justify-center shadow-lg shadow-amber-900/40"><Building2 size={16} /></div>
          <div>
            <div className="text-[9.5px] uppercase tracking-[0.22em] text-amber-400 font-bold font-mono-spec">codevoiture.fr</div>
            <div className="text-white text-[14px] font-semibold leading-tight">Secrétariat</div>
          </div>
        </div>
        <nav className="flex flex-col gap-1 p-3 flex-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = n.exact ? pathname === n.to : pathname.startsWith(n.to);
            return (
              <Link key={n.to} to={n.to} data-testid={`sec-side-${n.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className={`flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold transition ${active ? 'bg-amber-500 text-black shadow-md shadow-amber-900/40' : 'text-white/65 hover:bg-white/5 hover:text-white'}`}>
                <Icon size={15} /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <Link to="/?public=1" className="flex items-center gap-2.5 px-3.5 h-9 rounded-xl text-[12.5px] font-medium text-white/55 hover:bg-white/5 hover:text-white transition"><ExternalLink size={13} /> Voir le site public</Link>
          <button onClick={onLogout} data-testid="sec-logout" className="w-full flex items-center gap-2.5 px-3.5 h-9 rounded-xl text-[12.5px] font-medium text-white/55 hover:bg-red-950/40 hover:text-red-300 transition"><LogOut size={13} /> Déconnexion</button>
          {user && <div className="px-3.5 pt-2 pb-1 text-white/35 text-[11px] font-mono-spec truncate">{user.email}</div>}
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="h-12 md:h-14 border-b border-white/10 bg-[#0d0d0e]/60 backdrop-blur flex items-center justify-between gap-2 px-4 lg:px-8 sticky top-0 z-40">
          {pathname !== '/secretariat' && (
            <button
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/secretariat'))}
              data-testid="sec-mobile-back"
              aria-label="Retour"
              className="md:hidden shrink-0 -ml-1 w-9 h-9 rounded-lg border border-white/10 bg-white/5 text-white/80 active:scale-95 inline-flex items-center justify-center"
            >
              <ArrowLeft size={16} />
            </button>
          )}
          <div className="hidden md:block text-white/45 text-[11px] lg:text-[12px] font-mono-spec uppercase tracking-[0.2em]">Gestion auto-école</div>
          {/* Nav mobile horizontale */}
          <nav className="flex md:hidden items-center gap-1 overflow-x-auto flex-1 min-w-0">
            {NAV.map((n) => {
              const active = n.exact ? pathname === n.to : pathname.startsWith(n.to);
              return <Link key={n.to} to={n.to} className={`px-2.5 h-8 rounded-lg text-[11.5px] font-semibold inline-flex items-center whitespace-nowrap ${active ? 'bg-amber-500 text-black' : 'text-white/60'}`}>{n.label}</Link>;
            })}
          </nav>
          <button onClick={onLogout} data-testid="sec-mobile-logout" className="md:hidden shrink-0 text-red-300 text-[12px] inline-flex items-center gap-1"><LogOut size={12} /> Quitter</button>
          <span className="hidden md:block text-white/50 text-[12px] truncate max-w-[160px]">{user?.name}</span>
        </header>
        <main className="pb-10">{children}</main>
      </div>
    </div>
  );
}
